

export type LightingEffect = 'glow' | 'pulse' | 'rainbow';

export interface LightingState {
  intensity: number; // 0-1 (brightness level)
  effect: LightingEffect;
  fill?: boolean;
}

/**
 * BoardLighting - Controls all pinball-style lighting effects
 *
 * CUSTOMIZATION GUIDE:
 *
 * 🌈 HOLE RAINBOW EFFECTS (lines ~109-117):
 *   - marqueeSpeed: Controls how fast colors travel (0.2 = slower, 0.5 = faster)
 *   - phaseOffset: Staggers timing between 1pt, 3pt, 5pt holes
 *   - hue speed: (now * 0.3) - higher = faster color cycling
 *
 * 🎪 PERIMETER CHASER (lines ~235-245):
 *   - chaserSpeed: How fast lights chase around (0.003 = slow, 0.01 = fast)
 *   - lightSpacing: Distance between lights (25 = normal, 15 = dense, 40 = sparse)
 *   - lightSize: Radius of each light (4 = normal, 2 = small, 8 = large)
 *   - Color cycling: (now * 0.2 + i * 30) - adjust speed and spacing
 *
 * ✨ PULSE EFFECTS (line ~106):
 *   - Pulse speed: Math.sin(now * 0.008) - higher = faster pulsing
 *   - Pulse range: 0.7 + 0.3 * - adjusts min/max brightness
 *
 * 💡 GLOW INTENSITY (lines ~63-124):
 *   - Base intensity: Set via setHoleLighting(points, intensity, effect)
 *   - Rim brightness: globalAlpha controls outer glow strength
 */
export class BoardLighting {
  private holes = new Map<number, LightingState>();
  private zones = new Map<number, LightingState>();
  private showInProgress = false;
  private showStartTime = 0;
  private chaserEnabled = false;

  // 📝 PINBALL TEXT MESSAGING SYSTEM
  private messageQueue: Array<{
    lines: string[];  // Array of text lines for vertical stacking
    x: number;
    y: number;
    intensity: number;
    color: string;
    fontSize: number;
    startTime: number;
    duration: number;
    effect: 'glow' | 'pulse' | 'fade';
    rotation?: number;  // Rotation angle in radians for chevron text
    linePerspectiveShrink: number;
  }> = [];

  // 🎯 TURN INDICATOR LIGHTS
  private turnIndicatorActive = false;
  private turnIndicatorText = { left: 'YOUR', right: 'TURN' };

  // ⚽ BALLS LEFT DISPLAY
  private ballsLeft = 4;
  private ballsLeftVisible = true;
  private ballsLeftColor = '#FFD700'; // Default golden color, updated when game ball color is set
  private pendingBallsLeft: number | null = null; // Count to apply when rolling animation completes
  // Keep one indicator ball reserved (hidden) from rack to launch, until the
  // next decrement occurs. This keeps lights consistent through animation end
  // and while the ball is on the playfield.
  private reserveAtLaunch: boolean = false;

  /**
   * Explicitly control the launch reservation state. When true, one ball light
   * remains hidden to represent the ball sitting at the launch position until
   * the next decrement (i.e., after the roll completes).
   */
  public setReserveAtLaunch(active: boolean) {
    this.reserveAtLaunch = active;
  }

  // 🎯 BALL ROLLING ANIMATION
  private rollingBall: {
    active: boolean;
    startX: number;
    startY: number;
    targetX: number;
    targetY: number;
    startTime: number;
    duration: number;
    color: string;
  } | null = null;

  /**
   * Set lighting for holes of a specific point value
   * @param points Hole point value (1, 3, 5)
   * @param intensity Light intensity (0-1, 0 = off)
   * @param effect Lighting effect type
   * @param fill should the hole be filled... with light
   */
  setHoleLighting(points: number, intensity: number = 0, effect: LightingEffect = 'glow', fill:boolean = false) {
    if (intensity <= 0) {
      this.holes.delete(points);
    } else {
      this.holes.set(points, { intensity: Math.max(0, Math.min(1, intensity)), effect, fill });
    }
  }

  /**
   * Set lighting for score zones (chevrons)
   * @param points Zone point value (1, 3, 5)
   * @param intensity Light intensity (0-1, 0 = off)
   * @param effect Lighting effect type
   */
  setZoneLighting(points: number, intensity: number = 0, effect: LightingEffect = 'glow') {
    if (intensity <= 0) {
      this.zones.delete(points);
    } else {
      this.zones.set(points, { intensity: Math.max(0, Math.min(1, intensity)), effect });
    }
  }

  /**
   * Clear all lighting
   */
  clearAll() {
    this.holes.clear();
    this.zones.clear();
  }

  /**
   * 🎪 START PINBALL LIGHT SHOW
   *
   * CUSTOMIZATION: Adjust the intensity values and effects here
   * - Hole intensity: 0.8 = bright, 0.5 = medium, 1.0 = maximum
   * - Zone intensity: Usually 0.6-0.8 (slightly dimmer than holes)
   * - Effect types: 'rainbow' | 'pulse' | 'glow'
   */
  startPinballShow() {
    this.showInProgress = true;
    this.chaserEnabled = true;
    this.showStartTime = performance.now();

    // 🍺 GRUNGY BAR PINBALL LIGHTING: Subdued intensities for authentic feel
    // Grungy values: 0.3-0.5 = dim bar lighting, 0.6-0.8 = normal, 0.9+ = arcade bright
    [1, 3, 5].forEach(points => {
      this.setHoleLighting(points, 0.4, 'rainbow');  // 0.4 = dim grungy glow
      this.setZoneLighting(points, 0.3, 'rainbow');  // 0.3 = very subtle chevrons
    });
  }

  /**
   * Stop the light show (called when player starts rolling)
   */
  stopPinballShow() {
    this.showInProgress = false;
    this.chaserEnabled = false;
    this.clearAll();
  }

  /**
   * Check if light show is active
   */
  isShowActive(): boolean {
    return this.showInProgress;
  }

  /**
   * 💬 SHOW PINBALL MESSAGE - Display glowing text on the board
   *
   * @param text Message to display
   * @param x X position (0-1 as fraction of board width, or absolute pixels)
   * @param y Y position (0-1 as fraction of board height, or absolute pixels)
   * @param options Customization options
   */
  showMessage(text: string, x: number, y: number, options: {
    duration?: number;        // How long to show (ms), default 2000
    fontSize?: number;        // Text size, default 24
    color?: string;          // 'amber' | 'red' | 'blue' | 'green' | 'white' | hsl() string
    effect?: 'glow' | 'pulse' | 'fade';  // Animation effect
    intensity?: number;       // Glow brightness (0-1)
    rotation?: number;        // Rotation angle in radians for chevron text
    linePerspectiveShrink?: number; // Amount the perspective scale drops each line (0-1)
  } = {}) {
    const {
      duration = 2000,
      fontSize = 20,
      color = 'amber',
      effect = 'glow',
      intensity = 0.8,
      rotation
    } = options;

    // Convert color names to HSL
    const colorMap = {
      amber: 'hsl(35, 80%, 60%)',
      red: 'hsl(0, 80%, 60%)',
      blue: 'hsl(200, 80%, 60%)',
      green: 'hsl(120, 80%, 60%)',
      white: 'hsl(0, 0%, 90%)',
      orange: 'hsl(25, 80%, 60%)'
    };

    const finalColor = colorMap[color as keyof typeof colorMap] || color;
    const linePerspectiveShrink = Math.max(0, Math.min(1, options.linePerspectiveShrink ?? 0.15));

    // Split long text into multiple lines for vertical stacking
    const splitText = (text: string): string[] => {
      // If text is 10 characters or less, keep it as one line
      if (text.length <= 10) {
        return [text];
      }

      // Split on spaces and create lines that fit within ~10 characters
      const words = text.split(' ');
      const lines: string[] = [];
      let currentLine = '';

      for (const word of words) {
        if (currentLine.length === 0) {
          currentLine = word;
        } else if ((currentLine + ' ' + word).length <= 10) {
          currentLine += ' ' + word;
        } else {
          lines.push(currentLine);
          currentLine = word;
        }
      }

      if (currentLine.length > 0) {
        lines.push(currentLine);
      }

      return lines;
    };

    this.messageQueue.push({
      lines: splitText(text),
      x,
      y,
      intensity,
      color: finalColor,
      fontSize,
      startTime: performance.now(),
      duration,
      effect,
      rotation,
      linePerspectiveShrink
    });
  }

  /**
   * 🔤 CLEAR ALL MESSAGES
   */
  private tutorialIntroTimeout: ReturnType<typeof setTimeout> | null = null;
  private tutorialIntroCompletionTimeout: ReturnType<typeof setTimeout> | null = null;
  private tutorialIntroCompleteCallback: (() => void) | null = null;

  clearMessages() {
    this.messageQueue = [];
    this.clearTutorialIntroTimer();
  }

  private clearTutorialIntroTimer() {
    if (this.tutorialIntroTimeout !== null) {
      clearTimeout(this.tutorialIntroTimeout);
      this.tutorialIntroTimeout = null;
    }
    if (this.tutorialIntroCompletionTimeout !== null) {
      clearTimeout(this.tutorialIntroCompletionTimeout);
      this.tutorialIntroCompletionTimeout = null;
    }
    this.tutorialIntroCompleteCallback = null;
  }

  /**
   * 🎯 TURN INDICATOR CONTROL
   */
  setTurnIndicator(active: boolean, playerName?: string, isCurrentUser?: boolean) {
    this.turnIndicatorActive = active;
    
    if (active && playerName) {
      if (isCurrentUser) {
        // Show "YOUR" "TURN" for current user's turn
        this.turnIndicatorText = { left: 'YOUR', right: 'TURN' };        
      } else {
        // Show player's name for other player's turn
        // Truncate to 4 characters to prevent overflow
        const truncatedName = playerName.substring(0, 4).toUpperCase();
        this.turnIndicatorText = { left: truncatedName + "'S", right: 'TURN' };        
      }
    } else {
      // console.log('🎯 No text set - active:', active, 'playerName:', playerName);
    }
  }

  /**
   * ⚽ BALLS LEFT DISPLAY CONTROL
   */
  setBallsLeft(count: number) {
    // If rolling animation is active, defer this update to prevent snapping
    if (this.rollingBall?.active) {
      // Store the pending count to apply when animation completes
      this.pendingBallsLeft = Math.max(0, count);
      return;
    }
    const newCount = Math.max(0, count);
    // If the count is decreasing, the previous reserved ball has now been consumed
    if (newCount < this.ballsLeft) {
      this.reserveAtLaunch = false;
    }
    this.ballsLeft = newCount;
  }

  setBallsLeftVisible(visible: boolean) {
    this.ballsLeftVisible = visible;
  }

  setBallsLeftColor(color: string) {
    this.ballsLeftColor = color;
  }

  /**
   * ⚽ CHECK IF ROLLING ANIMATION IS ACTIVE
   */
  isRollingAnimationActive(): boolean {
    return this.rollingBall?.active || false;
  }

  /**
   * ⚽ STOP ROLLING ANIMATION - Clear any active rolling animation
   */
  stopRollingAnimation() {
    if (this.rollingBall) {
      this.rollingBall.active = false;
      this.rollingBall = null;
    }
    // Clear pending ball count without applying it
    // The caller will set the correct value immediately after stopping the animation
    this.pendingBallsLeft = null;
  }

  /**
   * 🎯 Start pinball-style ball rolling animation from balls left to launch area
   */
  startBallRollingAnimation(
    boardBounds: { x: number, y: number, width: number, height: number },
    ballColor: string,
    targetScreenPos?: { x: number, y: number }
  ) {
    // Update the balls left color to match the game ball
    this.setBallsLeftColor(ballColor);

    // Calculate start position (rightmost ball in balls left display)
    // 📝 MUST MATCH THE VALUES IN drawBallsLeft() METHOD
    const displayX = boardBounds.x + 10;  // Match drawBallsLeft positioning
    const displayY = boardBounds.y + boardBounds.height - 40;  // Match drawBallsLeft positioning
    const ballSpacing = 15;  // Match drawBallsLeft spacing
    const startX = displayX + ((this.ballsLeft - 1) * ballSpacing); // Position of rightmost ball
    const startY = displayY;

    // Use provided target position or default to center-bottom
    const targetX = targetScreenPos?.x || (boardBounds.x + (boardBounds.width * 0.5));
    const targetY = targetScreenPos?.y || (boardBounds.y + (boardBounds.height * 0.85));

    this.rollingBall = {
      active: true,
      startX,
      startY,
      targetX,
      targetY,
      startTime: performance.now(),
      duration: 800, // 800ms animation
      color: ballColor
    };

    // Do not mutate ballsLeft here. Instead, reserve one indicator ball
    // until the first decrement after this animation completes.
    this.reserveAtLaunch = true;
  }

  public isMessageQueueEmpty(): boolean {
    return this.messageQueue.length === 0;
  }


  /**
   * 🎯 SHOW POSITIONED MESSAGE - Display message with full positioning control
   * Handles messages from the unified system with positioning data
   */
  public showPositionedMessage(text: string, messageData: any) {
    // For multi-part messages with delay, use the full text array; otherwise split by spaces
    let words: string[];
    if (messageData.type === 'multi-part' && messageData.positioning?.delay) {
      words = messageData.text; // Use the full array for time-based multi-part
    } else if (messageData.type === 'roll-feedback') {
      words = messageData.text; // Use the full array for roll-feedback messages
    } else {
      words = text.split(' '); // Split single text for regular messages
    }

    const positioning = messageData.positioning || {};

    // 📍 POSITIONING MODES
    const mode = positioning.mode || 'random';
    const baseStartX = positioning.startX || (mode === 'random' ? Math.random() * 0.2 + 0.4 : 0.5);
    const baseStartY = positioning.startY || (mode === 'random' ? Math.random() * 0.2 + 0.4 : 0.5);
    const verticalSpacing = positioning.verticalSpacing || 0.05;
    const delay = positioning.delay || 800;
    const duration = positioning.duration || 1500;
    const color = positioning.color || 'amber';
    const fontSize = positioning.fontSize || 24;
    const effect = positioning.effect || 'glow';

    if (mode === 'fixed') {
      // FIXED MODE: All words appear at the same position, replacing each other
      const lineShrinkPercent = Math.max(0, Math.min(100, positioning.lineShrink ?? 0));
      const shrinkFactor = 1 - (lineShrinkPercent / 100);
      const linePerspectiveShrink = Math.max(0, Math.min(1, positioning.linePerspectiveShrink ?? 0.15));
      words.forEach((word, i) => {
        setTimeout(() => {
          const lineFontSize = lineShrinkPercent > 0
            ? fontSize * Math.pow(shrinkFactor, i)
            : fontSize;
           this.showMessage(word, baseStartX, baseStartY, {
             duration,
             fontSize: lineFontSize,
             color,
             effect,
             linePerspectiveShrink
           });
        }, i * delay);
      });
    } else if (mode === 'staggered') {
      // STAGGERED MODE: Words appear vertically stacked with precise control
      // Perfect for "FLICK" -> "TO" -> "ROLL" effect
      words.forEach((word, i) => {
        setTimeout(() => {
          const x = baseStartX;
          const y = baseStartY + (i * verticalSpacing); // Stack vertically downward
          this.showMessage(word, x, y, {
            duration,
            fontSize,
            color,
            effect
          });
        }, i * delay);
      });
    } else if (mode === 'reverse-stagger') {
      // REVERSE-STAGGER MODE: Words appear vertically stacked upward
      // Perfect for building upward momentum
      words.forEach((word, i) => {
        setTimeout(() => {
          const x = baseStartX;
          const y = baseStartY - (i * verticalSpacing); // Stack vertically upward
          this.showMessage(word, x, y, {
            duration,
            fontSize,
            color,
            effect
          });
        }, i * delay);
      });
    } else if (mode === 'reading') {
      // READING MODE: Words appear left-to-right, then wrap to next line
      // Perfect for natural text flow like "Keep calm" -> new line "play more" -> new line "skrollrollr!"
      const wordsPerLine = positioning.wordsPerLine || 2; // Configurable words per line
      const horizontalSpacing = positioning.horizontalSpacing || 0.15; // Space between words on same line
      const lineSpacing = positioning.lineSpacing || verticalSpacing; // Space between lines

      words.forEach((word, i) => {
        setTimeout(() => {
          const lineIndex = Math.floor(i / wordsPerLine);
          const wordInLineIndex = i % wordsPerLine;

          // Calculate position
          const x = baseStartX + (wordInLineIndex * horizontalSpacing) - ((wordsPerLine - 1) * horizontalSpacing / 2);
          const y = baseStartY + (lineIndex * lineSpacing);

          this.showMessage(word, x, y, {
            duration,
            fontSize,
            color,
            effect
          });
        }, i * delay);
      });
    } else if (mode === 'roll-zones') {
      // ROLL-ZONES MODE: Dedicated zones for immediate roll feedback
      // Used by roll-feedback type messages for consistent player feedback locations
      if (messageData.type === 'roll-feedback') {
        // 🎯 FINAL CHEVRON POSITIONING - Tuned to match actual chevrons
        (window as any).CHEVRON_DEBUG = (window as any).CHEVRON_DEBUG || {
          leftX: 0.25,
          leftY: 0.265,
          rightX: 0.75,
          rightY: 0.265,
          leftRotation: 0.555,
          rightRotation: -0.555,
          verticalSpacing: 0.05,
          showGuides: false  // Set to true to show debug guides again
        };

        // Define feedback zone positions
        const leftZone = { x: 0.18, y: 0.02 };    // Upper left corner
        const rightZone = { x: 0.82, y: 0.02 };   // Upper right corner
        const chevronLeft = { x: (window as any).CHEVRON_DEBUG.leftX, y: (window as any).CHEVRON_DEBUG.leftY };
        const chevronRight = { x: (window as any).CHEVRON_DEBUG.rightX, y: (window as any).CHEVRON_DEBUG.rightY };

        // Check if a specific zone is requested first
        if (messageData.rollFeedback?.zone) {
          const zone = messageData.rollFeedback.zone;
          let zonePos: { x: number; y: number };

          switch (zone) {
            case 'right':
              zonePos = rightZone;
              break;
            case 'chevron-left':
              zonePos = chevronLeft;
              break;
            case 'chevron-right':
              zonePos = chevronRight;
              break;
            case 'left':
            default:
              zonePos = leftZone;
          }

          // Add rotation for chevron zones (using debug values)
          const rotation = zone === 'chevron-left' ? (window as any).CHEVRON_DEBUG.leftRotation :
                          zone === 'chevron-right' ? (window as any).CHEVRON_DEBUG.rightRotation : undefined;


          // Special handling for two-word chevron messages: first word on left chevron, second on right
          if (words.length === 2 && (zone === 'chevron-left' || zone === 'chevron-right')) {

            // First word on left chevron
            this.showMessage(words[0], chevronLeft.x, chevronLeft.y, {
              duration,
              fontSize,
              color,
              effect,
              rotation: (window as any).CHEVRON_DEBUG.leftRotation
            });

            // Second word on right chevron
            this.showMessage(words[1], chevronRight.x, chevronRight.y, {
              duration,
              fontSize,
              color,
              effect,
              rotation: (window as any).CHEVRON_DEBUG.rightRotation
            });
          } else {
            // Single word or 3+ words: show all in the specified zone (stacked vertically if multiple)
            words.forEach((word, i) => {
              this.showMessage(word, zonePos.x, zonePos.y + (i * (window as any).CHEVRON_DEBUG.verticalSpacing), {
                duration,
                fontSize,
                color,
                effect,
                rotation
              });
            });
          }

        } else if (words.length === 2) {
          // Auto-split words for dual-zone display (e.g., "SLOW DOWN") only if no specific zone requested
          this.showMessage(words[0], leftZone.x, leftZone.y, {
            duration,
            fontSize,
            color,
            effect
          });
          this.showMessage(words[1], rightZone.x, rightZone.y, {
            duration,
            fontSize,
            color,
            effect
          });
        } else {
          // 3+ words: distribute across zones
          words.forEach((word, i) => {
            const isEven = i % 2 === 0;
            const zonePos = isEven ? leftZone : rightZone;

            this.showMessage(word, zonePos.x, zonePos.y + (Math.floor(i / 2) * 0.05), {
              duration,
              fontSize,
              color,
              effect
            });
          });
        }
      } else {
        // Fallback to random if not properly configured
        words.forEach((word, i) => {
          setTimeout(() => {
            const x = Math.random() * 0.4 + 0.3;
            const y = baseStartY + (i * verticalSpacing);
            this.showMessage(word, x, y, {
              duration,
              fontSize,
              color,
              effect
            });
          }, i * delay);
        });
      }
    } else {
      // RANDOM MODE: Default behavior with random positioning
      words.forEach((word, i) => {
        setTimeout(() => {
          const x = Math.random() * 0.2 + 0.4;
          const y = baseStartY + (i * verticalSpacing);
          this.showMessage(word, x, y, {
            duration,
            fontSize,
            color,
            effect
          });
        }, i * delay);
      });
    }
  }

  /**
   * 🥊 MATCH INTRODUCTION - Show VS screen on board
   */
  showMatchIntro(player1Name: string, aiName: string, duration: number = 4000) {
    // Clear any existing messages
    this.clearMessages();

    // Show VS match-up in center of board
    this.showMessage(player1Name.toUpperCase(), 0.5, 0.45, {
      fontSize: 22,
      color: 'amber',
      effect: 'glow',
      duration: duration
    });

    this.showMessage("VS", 0.5, 0.5, {
      fontSize: 18,
      color: 'white',
      effect: 'pulse',
      duration: duration
    });

    this.showMessage(aiName.toUpperCase(), 0.5, 0.58, {
      fontSize: 22,
      color: 'blue',
      effect: 'glow',
      duration: duration
    });
  }

  /**
   * 🎓 TUTORIAL INTRODUCTION - Show welcome messages
   */
  showTutorialIntro(duration: number = 5000, onComplete?: () => void) {
    this.clearMessages();
    const firstDuration = duration;
    this.showMessage('How to play SkrollrRollr!', 0.5, 0.45, {
      fontSize: 36,
      color: 'white',
      effect: 'glow',
      duration: firstDuration
    });

    this.clearTutorialIntroTimer();
    const delay = 2000;
    const secondDuration = Math.max(1500, duration - delay);
    this.tutorialIntroTimeout = setTimeout(() => {
      this.showMessage('Skroll to roll!', 0.5, 0.58, {
        fontSize: 30,
        color: 'lime',
        effect: 'pulse',
        duration: secondDuration
      });
      this.tutorialIntroTimeout = null;
      if (onComplete) {
        this.tutorialIntroCompleteCallback = onComplete;
        this.tutorialIntroCompletionTimeout = setTimeout(() => {
          this.tutorialIntroCompleteCallback?.();
          this.tutorialIntroCompleteCallback = null;
          this.tutorialIntroCompletionTimeout = null;
        }, secondDuration);
      }
    }, delay);
  }


  /**
   * ⚔️ RETRIBUTION ROUND - Show retribution message on board
   */
  showRetributionMessage(playerName: string, duration: number = 4000) {
    this.clearMessages();

    this.showMessage("RETRIBUTION", 0.5, 0.35, {
      fontSize: 22,
      color: 'red',
      effect: 'pulse',
      duration: duration
    });

    this.showMessage("ROUND", 0.5, 0.45, {
      fontSize: 20,
      color: 'red',
      effect: 'pulse',
      duration: duration
    });

    this.showMessage("LAST", 0.5, 0.58, {
      fontSize: 18,
      color: 'orange',
      effect: 'glow',
      duration: duration
    });

    this.showMessage("CHANCE", 0.5, 0.65, {
      fontSize: 18,
      color: 'orange',
      effect: 'glow',
      duration: duration
    });

    this.showMessage("FOR", 0.5, 0.72, {
      fontSize: 16,
      color: 'orange',
      effect: 'glow',
      duration: duration
    });

    this.showMessage(playerName.toUpperCase(), 0.5, 0.79, {
      fontSize: 20,
      color: 'white',
      effect: 'glow',
      duration: duration
    });
  }

  /**
   * ⚡ SUDDEN DEATH - Show sudden death message on board
   */
  showSuddenDeathMessage(roundNumber: number, playerName: string, duration: number = 4000) {
    this.clearMessages();

    this.showMessage("SUDDEN DEATH", 0.5, 0.35, {
      fontSize: 24,
      color: 'red',
      effect: 'pulse',
      duration: duration
    });

    this.showMessage(`ROUND ${roundNumber}`, 0.5, 0.45, {
      fontSize: 20,
      color: 'red',
      effect: 'pulse',
      duration: duration
    });

    this.showMessage("BOTH TIED AT 21!", 0.5, 0.58, {
      fontSize: 16,
      color: 'orange',
      effect: 'glow',
      duration: duration
    });

    this.showMessage(`${playerName.toUpperCase()} STARTS`, 0.5, 0.68, {
      fontSize: 18,
      color: 'white',
      effect: 'glow',
      duration: duration
    });

    this.showMessage("HIGHEST SCORE WINS!", 0.5, 0.78, {
      fontSize: 16,
      color: 'amber',
      effect: 'glow',
      duration: duration
    });
  }

  /**
   * 💥 BUST MESSAGE - Show when player busts
   */
  showBustMessage(playerName: string, bustTo: number, duration: number = 3000) {
    this.clearMessages();

    this.showMessage(playerName.toUpperCase(), 0.5, 0.45, {
      fontSize: 22,
      color: 'red',
      effect: 'pulse',
      duration: duration
    });

    this.showMessage("BUSTS", 0.5, 0.55, {
      fontSize: 26,
      color: 'red',
      effect: 'pulse',
      duration: duration
    });

    this.showMessage("😱", 0.5, 0.65, {
      fontSize: 30,
      color: 'red',
      effect: 'pulse',
      duration: duration
    });

    this.showMessage(`BACK TO ${bustTo}`, 0.5, 0.75, {
      fontSize: 16,
      color: 'orange',
      effect: 'glow',
      duration: duration
    });
  }

  /**
   * 🏆 GAME OVER MESSAGE - Show win/loss on board
   */
  showGameOverMessage(isWinner: boolean, winnerName: string, loserName?: string, winnerScore?: number, loserScore?: number, isMultiplayer = false) {
    this.clearMessages();

    if (isWinner) {
      // Victory messages
      this.showMessage("VICTORY!", 0.5, 0.35, {
        fontSize: 28,
        color: 'green',
        effect: 'pulse',
        duration: Infinity // Stay forever
      });

      if (isMultiplayer && loserName) {
        this.showMessage(`YOU BEAT`, 0.5, 0.45, {
          fontSize: 18,
          color: 'white',
          effect: 'glow',
          duration: Infinity // Stay forever
        });

        this.showMessage(loserName.toUpperCase(), 0.5, 0.55, {
          fontSize: 20,
          color: 'amber',
          effect: 'glow',
          duration: Infinity // Stay forever
        });
      } else {
        this.showMessage("YOU HIT 21!", 0.5, 0.5, {
          fontSize: 22,
          color: 'amber',
          effect: 'glow',
          duration: Infinity // Stay forever
        });
      }
    } else {
      // Loss messages
      this.showMessage("GAME", 0.5, 0.3, {
        fontSize: 28,
        color: 'red',
        effect: 'pulse',
        duration: Infinity // Stay forever
      });
      this.showMessage("OVER", 0.5, 0.4, {
        fontSize: 28,
        color: 'red',
        effect: 'pulse',
        duration: Infinity // Stay forever
      });

      this.showMessage(`${winnerName.toUpperCase()} WINS!`, 0.5, 0.5, {
        fontSize: 20,
        color: 'orange',
        effect: 'glow',
        duration: Infinity // Stay forever
      });
    }

    // Show scores if multiplayer
    if (isMultiplayer && winnerScore !== undefined && loserScore !== undefined) {
      this.showMessage(`${winnerScore} - ${loserScore}`, 0.5, 0.65, {
        fontSize: 16,
        color: 'white',
        effect: 'glow',
        duration: Infinity // Stay forever
      });
    }

    // Arrow pointing to menu (bottom right)
    this.showMessage("MENU ⤷", 0.80, 0.95, {
      fontSize: 16,
      color: 'amber',
      effect: 'pulse',
      duration: Infinity // Stay forever
    });
  }

  /**
   * Draw a hole with lighting effects
   */
  drawHoleWithLighting(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, pts: number) {
    const lighting = this.holes.get(pts);

    // 1. Draw outer ring
    ctx.beginPath();
    ctx.arc(cx, cy, r + 3, 0, Math.PI * 2);
    ctx.fillStyle = '#140f0b';
    ctx.fill();

    // 2. Draw hole interior (black background), covering any glow bleed
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = '#000';
    ctx.fill();

    // 3. Draw lighting effects if active
    if (lighting && lighting.intensity > 0) {
      ctx.save();
      const alpha = lighting.intensity; // This will be 1.0 in your scenario
      const now = performance.now();
      
      let glowColor = '#ffffff';
      let pulseIntensity = 1; // Default for 'glow', but used by 'pulse'

      if (lighting.effect === 'pulse') {
        pulseIntensity = 0.7 + 0.3 * Math.sin(now * 0.008);
      } else if (lighting.effect === 'rainbow') {
        // For rainbow, we just cycle the color. The brightness is now controlled by `alpha`.
        const hue = (now * 0.1) % 360;
        glowColor = `hsl(${hue}, 80%, 60%)`;
      }

      // --- Outer Glow ---
      const gradient = ctx.createRadialGradient(cx, cy, r, cx, cy, r + 15);
      const finalGlowAlpha = alpha * pulseIntensity; // Use pulseIntensity only for pulse effect

      if (lighting.effect === 'rainbow') {
        gradient.addColorStop(0, glowColor.replace('hsl', 'hsla').replace(')', `, ${alpha * 0.8})`)); // Use full alpha
        gradient.addColorStop(0.5, glowColor.replace('hsl', 'hsla').replace(')', `, ${alpha * 0.3})`));
        gradient.addColorStop(1, glowColor.replace('hsl', 'hsla').replace(')', `, 0)`));
      } else {
        gradient.addColorStop(0, `rgba(255, 255, 255, ${finalGlowAlpha * 0.9})`);
        gradient.addColorStop(0.5, `rgba(255, 255, 255, ${finalGlowAlpha * 0.4})`);
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      }
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(cx, cy, r + 15, 0, Math.PI * 2);
      ctx.fill();
      
      // --- "Flashlight" Fill ---
      if (lighting.fill) {
        ctx.fillStyle = `rgba(255, 255, 255, ${0.3 + (alpha * 0.3)})`; // Link brightness to intensity
        ctx.beginPath();
        ctx.arc(cx, cy, r - 2, 0, Math.PI * 2);
        ctx.fill();
      }

      // --- Rim Effect ---
      if (lighting.effect === 'rainbow') {
          ctx.lineWidth = 3;
          const segmentCount = 18;
          const rotation = (now / 500) * Math.PI;
          for (let i = 0; i < segmentCount; i++) {
              const startAngle = rotation + (i / segmentCount) * Math.PI * 2;
              const endAngle = rotation + ((i + 1) / segmentCount) * Math.PI * 2;
              const hue = ((now / 10) + (i / segmentCount) * 360) % 360;
              ctx.strokeStyle = `hsl(${hue}, 100%, 70%)`;
              ctx.globalAlpha = alpha; // Use the full intensity
              ctx.beginPath();
              ctx.arc(cx, cy, r + 1.5, startAngle, endAngle);
              ctx.stroke();
          }
      } else {
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;
          ctx.globalAlpha = alpha * pulseIntensity;
          ctx.beginPath();
          ctx.arc(cx, cy, r + 1, 0, Math.PI * 2);
          ctx.stroke();
      }

      ctx.restore();
    }
  }

  /**
   * Draw a chevron with lighting effects
   */
  drawChevronWithLighting(ctx: CanvasRenderingContext2D, slab: any, ny: number, leftEdge: number, rightEdge: number, apexDepth: number, points: number) {
    const y = slab.y + ny * slab.h;
    const lighting = this.zones.get(points);

    // Draw lighting glow behind chevron if active
    if (lighting && lighting.intensity > 0) {
      ctx.save();
      const alpha = lighting.intensity;
      const now = performance.now();

      let glowColor = '#ffffff';
      let pulseIntensity = 1;

      if (lighting.effect === 'pulse') {
        pulseIntensity = 0.7 + 0.3 * Math.sin(now * 0.008);
      } else if (lighting.effect === 'rainbow') {
        // For marquee effect, cycle the brightness based on zone position
        const marqueeSpeed = 0.002; // Same speed as holes
        const phaseOffset = points === 1 ? 0 : points === 3 ? 2.1 : 4.2; // Same stagger
        const marqueeCycle = (now * marqueeSpeed + phaseOffset) % (Math.PI * 2);

        // Use sine wave to create the traveling light effect
        const marqueeIntensity = 0.3 + 0.7 * Math.max(0, Math.sin(marqueeCycle));

        const hue = (now * 0.3) % 360;
        glowColor = `hsl(${hue}, 100%, 70%)`;
        pulseIntensity = marqueeIntensity;
      }

      // Create chevron path for glow
      ctx.beginPath();
      ctx.moveTo(slab.x + slab.w * leftEdge, y);
      ctx.lineTo(slab.x + slab.w * 0.50, y + slab.h * apexDepth);
      ctx.lineTo(slab.x + slab.w * rightEdge, y);

      // Draw glowing stroke
      ctx.strokeStyle = lighting.effect === 'rainbow' ? glowColor : '#ffffff';
      ctx.lineWidth = 8;
      ctx.globalAlpha = alpha * pulseIntensity * 0.6;
      ctx.stroke();

      // Draw inner bright line
      ctx.strokeStyle = lighting.effect === 'rainbow' ? glowColor : '#ffffff';
      ctx.lineWidth = 3;
      ctx.globalAlpha = alpha * pulseIntensity;
      ctx.stroke();

      ctx.restore();
    }

    // Draw regular chevron
    ctx.beginPath();
    ctx.moveTo(slab.x + slab.w * leftEdge, y);
    ctx.lineTo(slab.x + slab.w * 0.50, y + slab.h * apexDepth);
    ctx.lineTo(slab.x + slab.w * rightEdge, y);
    ctx.stroke();
  }

  /**
   * 🎪 PERIMETER CHASER LIGHTS - Fixed bulbs that blink ON/OFF in sequence
   *
   * CUSTOMIZATION PARAMETERS:
   * - chaserSpeed: How fast the ON/OFF pattern travels
   * - lightsPerSide: Number of bulbs per side (width/4, height/4)
   * - lightSize: Size of each bulb
   * - chaserLength: How many consecutive bulbs are lit at once
   */
  drawPerimeterChaser(ctx: CanvasRenderingContext2D, boardRect: { x: number; y: number; w: number; h: number }) {
    if (!this.chaserEnabled) return;

    const now = performance.now();

    // 🏎️ CHASER SPEED: How fast the ON/OFF pattern travels
    // 0.00005 = very slow, 0.0002 = normal, 0.001 = fast
    const chaserSpeed = 0.0005;

    // 💡 BULB DENSITY: Number of fixed bulbs per side
    // Use frame dimensions divided by this factor
    const lightsPerSide = {
      horizontal: Math.floor(boardRect.w / 25), // 30px spacing = denser, 50px = sparser
      vertical: Math.floor(boardRect.h / 40)     // 40px spacing
    };

    // 🔵 LIGHT SIZE: Radius of each bulb
    // 2 = tiny, 3 = normal, 5 = large
    const lightSize = 5;

    // 🔥 CHASER PATTERN: How many consecutive bulbs are lit
    // 1 = single dot, 3 = small group, 5 = longer trail
    const chaserLength = 5;

    // 🌈 BULB COLOR - Grungy bar aesthetic
    const hue = 35;           // Amber
    const saturation = 50;    // Muted
    const lightness = 45;     // Dim

    // Create fixed bulb positions
    const bulbs: Array<{ x: number; y: number; index: number }> = [];

    // Top edge bulbs
    for (let i = 0; i < lightsPerSide.horizontal; i++) {
      const x = boardRect.x + (i / (lightsPerSide.horizontal - 1)) * boardRect.w;
      bulbs.push({ x, y: boardRect.y, index: bulbs.length });
    }

    // Right edge bulbs (skip corner to avoid duplicate)
    for (let i = 1; i < lightsPerSide.vertical; i++) {
      const y = boardRect.y + (i / (lightsPerSide.vertical - 1)) * boardRect.h;
      bulbs.push({ x: boardRect.x + boardRect.w, y, index: bulbs.length });
    }

    // Bottom edge bulbs (right to left, skip corner)
    for (let i = lightsPerSide.horizontal - 2; i >= 0; i--) {
      const x = boardRect.x + (i / (lightsPerSide.horizontal - 1)) * boardRect.w;
      bulbs.push({ x, y: boardRect.y + boardRect.h, index: bulbs.length });
    }

    // Left edge bulbs (bottom to top, skip both corners)
    for (let i = lightsPerSide.vertical - 2; i > 0; i--) {
      const y = boardRect.y + (i / (lightsPerSide.vertical - 1)) * boardRect.h;
      bulbs.push({ x: boardRect.x, y, index: bulbs.length });
    }

    const totalBulbs = bulbs.length;

    // Calculate which bulbs should be ON
    const chaserPosition = (now * chaserSpeed) % 1;
    const currentBulbIndex = Math.floor(chaserPosition * totalBulbs);

    ctx.save();

    bulbs.forEach((bulb, i) => {
      // Determine if this bulb should be ON
      let isOn = false;
      for (let j = 0; j < chaserLength; j++) {
        const activeIndex = (currentBulbIndex + j) % totalBulbs;
        if (i === activeIndex) {
          isOn = true;
          break;
        }
      }

      if (isOn) {
        // 💡 DRAW LIT BULB with glow
        const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;

        // Outer glow
        const gradient = ctx.createRadialGradient(bulb.x, bulb.y, 0, bulb.x, bulb.y, lightSize * 2.5);
        gradient.addColorStop(0, color.replace('hsl', 'hsla').replace(')', ', 0.8)'));
        gradient.addColorStop(0.6, color.replace('hsl', 'hsla').replace(')', ', 0.3)'));
        gradient.addColorStop(1, color.replace('hsl', 'hsla').replace(')', ', 0)'));

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(bulb.x, bulb.y, lightSize * 2.5, 0, Math.PI * 2);
        ctx.fill();

        // Bright center
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(bulb.x, bulb.y, lightSize, 0, Math.PI * 2);
        ctx.fill();
      } else {
        // 🔘 DRAW DARK BULB (optional - shows bulb positions when off)
        // Uncomment to see bulb positions:
        // ctx.fillStyle = 'rgba(100, 80, 60, 0.1)';
        // ctx.beginPath();
        // ctx.arc(bulb.x, bulb.y, lightSize * 0.5, 0, Math.PI * 2);
        // ctx.fill();
      }
    });

    ctx.restore();
  }

  /**
   * 🔧 DRAW DEBUG CHEVRON GUIDES - Temporary positioning helpers
   */
  private drawChevronDebugGuides(ctx: CanvasRenderingContext2D, boardRect: { x: number; y: number; w: number; h: number }) {
    // Only draw if debug mode is enabled
    if (!(window as any).CHEVRON_DEBUG?.showGuides) return;

    const debug = (window as any).CHEVRON_DEBUG;

    ctx.save();

    // Calculate positions
    const leftX = boardRect.x + debug.leftX * boardRect.w;
    const leftY = boardRect.y + debug.leftY * boardRect.h;
    const rightX = boardRect.x + debug.rightX * boardRect.w;
    const rightY = boardRect.y + debug.rightY * boardRect.h;

    // Set up font for debug text
    ctx.font = `bold 24px 'Arial Black', Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Draw left chevron guide
    ctx.save();
    ctx.translate(leftX, leftY);
    ctx.rotate(debug.leftRotation);

    // Glow effect for visibility
    ctx.shadowColor = 'hsl(120, 80%, 60%)';
    ctx.shadowBlur = 15;
    ctx.fillStyle = 'hsl(120, 80%, 60%)';
    ctx.fillText('LEFT', 0, 0);

    // Bright center
    ctx.shadowBlur = 0;
    ctx.fillStyle = 'hsl(120, 80%, 90%)';
    ctx.fillText('LEFT', 0, 0);

    ctx.restore();

    // Draw right chevron guide
    ctx.save();
    ctx.translate(rightX, rightY);
    ctx.rotate(debug.rightRotation);

    // Glow effect for visibility
    ctx.shadowColor = 'hsl(0, 80%, 60%)';
    ctx.shadowBlur = 15;
    ctx.fillStyle = 'hsl(0, 80%, 60%)';
    ctx.fillText('RIGHT', 0, 0);

    // Bright center
    ctx.shadowBlur = 0;
    ctx.fillStyle = 'hsl(0, 80%, 90%)';
    ctx.fillText('RIGHT', 0, 0);

    ctx.restore();

    // Draw positioning crosshairs
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.lineWidth = 1;

    // Left crosshair
    ctx.beginPath();
    ctx.moveTo(leftX - 10, leftY);
    ctx.lineTo(leftX + 10, leftY);
    ctx.moveTo(leftX, leftY - 10);
    ctx.lineTo(leftX, leftY + 10);
    ctx.stroke();

    // Right crosshair
    ctx.beginPath();
    ctx.moveTo(rightX - 10, rightY);
    ctx.lineTo(rightX + 10, rightY);
    ctx.moveTo(rightX, rightY - 10);
    ctx.lineTo(rightX, rightY + 10);
    ctx.stroke();

    ctx.restore();
  }

  /**
   * 🔤 DRAW PINBALL MESSAGES - Render all active text with glow effects
   */
  drawMessages(ctx: CanvasRenderingContext2D, boardRect: { x: number; y: number; w: number; h: number }) {
    const now = performance.now();

    // Clean up expired messages
    this.messageQueue = this.messageQueue.filter(msg =>
      now - msg.startTime < msg.duration
    );

    this.messageQueue.forEach(msg => {
      const elapsed = now - msg.startTime;
      const progress = elapsed / msg.duration;

      // Calculate position (support both fractional 0-1 and absolute pixel coordinates)
      let x = msg.x <= 1 ? boardRect.x + msg.x * boardRect.w : msg.x;
      const y = msg.y <= 1 ? boardRect.y + msg.y * boardRect.h : msg.y;

      // 📱 CLAMP HORIZONTAL POSITION - Keep messages within visible bounds
      // Add padding to account for moat and prevent text from touching edges
      // Moat is roughly 15% on each side, add extra buffer for text width
      const horizontalPadding = boardRect.w * 0.18;
      x = Math.max(boardRect.x + horizontalPadding, Math.min(x, boardRect.x + boardRect.w - horizontalPadding));


      // Calculate alpha based on effect
      let alpha = msg.intensity;
      if (msg.effect === 'fade') {
        alpha = msg.intensity * (1 - progress); // Fade out over time
      } else if (msg.effect === 'pulse') {
        alpha = msg.intensity * (0.6 + 0.4 * Math.sin(elapsed * 0.01)); // Pulse effect
      }

      ctx.save();

      // 📱 RESPONSIVE FONT SIZE - Scale down large fonts on narrow screens
      // Base breakpoint: 400px width is typical phone, scale linearly below 500px
      const baseFontSize = msg.fontSize;
      const scaleFactor = Math.min(1, boardRect.w / 500); // Scale down if board width < 500px
      const responsiveFontSize = Math.max(baseFontSize * scaleFactor, 12); // Don't go below 12px

      // Set up font
      ctx.font = `bold ${responsiveFontSize}px 'Arial Black', Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // 🌟 GLOW EFFECT - Multiple layers for authentic pinball glow
      // Draw each line vertically stacked with Star Wars perspective

      const lineSpacing = responsiveFontSize * 1.2; // Space between lines
      const totalHeight = (msg.lines.length - 1) * lineSpacing;
      const startY = y - totalHeight / 2; // Center the multi-line text vertically

      msg.lines.forEach((line, index) => {
        const lineY = startY + index * lineSpacing;

        // 🎬 STAR WARS PERSPECTIVE - Each line gets smaller as it goes "up" (further away)
        const perspectivesShrink = Math.max(0, Math.min(1, msg.linePerspectiveShrink));
        const perspectiveFactor = 1 - (index * perspectivesShrink); // Each line smaller than the one below
        const lineScale = Math.max(0.3, perspectiveFactor); // Don't shrink below 30%

        ctx.save(); // Save state for this line

        // Apply perspective scaling centered on the text position
        ctx.translate(x, lineY); // Move to text position
        ctx.scale(lineScale, lineScale); // Scale down for perspective

        // Apply rotation if specified (for chevron messages)
        if (msg.rotation !== undefined) {
          ctx.rotate(msg.rotation);
        }

        ctx.translate(-x, -lineY); // Move back

        // Outer glow (largest, most transparent)
        ctx.shadowColor = msg.color;
        ctx.shadowBlur = 20;
        ctx.fillStyle = msg.color.replace('hsl', 'hsla').replace(')', `, ${alpha * 0.3})`);
        ctx.fillText(line, x, lineY);

        // Middle glow
        ctx.shadowBlur = 10;
        ctx.fillStyle = msg.color.replace('hsl', 'hsla').replace(')', `, ${alpha * 0.6})`);
        ctx.fillText(line, x, lineY);

        // Inner bright core
        ctx.shadowBlur = 5;
        ctx.fillStyle = msg.color.replace('hsl', 'hsla').replace(')', `, ${alpha})`);
        ctx.fillText(line, x, lineY);

        // Bright center (no shadow)
        ctx.shadowBlur = 0;
        ctx.fillStyle = msg.color.replace('hsl', 'hsla').replace(')', `, ${alpha})`);
        ctx.fillText(line, x, lineY);

        ctx.restore(); // Restore state for this line
      });

      ctx.restore();
    });

    // 🔧 Draw debug guides for chevron positioning
    this.drawChevronDebugGuides(ctx, boardRect);
  }

  /**
   * 🎯 DRAW TURN INDICATOR LIGHTS - "YOUR" and "TURN" in corners
   */
  drawTurnIndicators(ctx: CanvasRenderingContext2D, boardRect: { x: number; y: number; w: number; h: number }) {
    if (!this.turnIndicatorActive) {      
      return;
    }
    
    const now = performance.now();

    // 💡 TURN INDICATOR STYLING
    const fontSize = 16;
    const color = 'hsl(35, 80%, 60%)'; // Amber
    const pulseIntensity = 0.7 + 0.3 * Math.sin(now * 0.008); // Slow pulse

    ctx.save();
    ctx.font = `bold ${fontSize}px 'Arial Black', Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Left text (YOUR or player name) - adjust positioning based on text length
    // For opponent names, shift right to prevent cutoff on the left
    const isOpponentTurn = this.turnIndicatorText.left !== 'YOUR';
    const leftPadding = isOpponentTurn ? 50 : 40; // Extra padding for opponent names
    const yourX = boardRect.x + leftPadding;
    const yourY = boardRect.y + boardRect.h - 10;

    // Multi-layer glow effect
    ctx.shadowColor = color;
    ctx.shadowBlur = 15;
    ctx.fillStyle = color.replace('hsl', 'hsla').replace(')', `, ${pulseIntensity * 0.4})`);
    ctx.fillText(this.turnIndicatorText.left, yourX, yourY);

    ctx.shadowBlur = 8;
    ctx.fillStyle = color.replace('hsl', 'hsla').replace(')', `, ${pulseIntensity * 0.7})`);
    ctx.fillText(this.turnIndicatorText.left, yourX, yourY);

    ctx.shadowBlur = 0;
    ctx.fillStyle = color.replace('hsl', 'hsla').replace(')', `, ${pulseIntensity})`);
    ctx.fillText(this.turnIndicatorText.left, yourX, yourY);

    // "TURN" in bottom right
    const turnX = boardRect.x + boardRect.w - 40;
    const turnY = boardRect.y + boardRect.h - 10;

    ctx.shadowColor = color;
    ctx.shadowBlur = 15;
    ctx.fillStyle = color.replace('hsl', 'hsla').replace(')', `, ${pulseIntensity * 0.4})`);
    ctx.fillText(this.turnIndicatorText.right, turnX, turnY);

    ctx.shadowBlur = 8;
    ctx.fillStyle = color.replace('hsl', 'hsla').replace(')', `, ${pulseIntensity * 0.7})`);
    ctx.fillText(this.turnIndicatorText.right, turnX, turnY);

    ctx.shadowBlur = 0;
    ctx.fillStyle = color.replace('hsl', 'hsla').replace(')', `, ${pulseIntensity})`);
    ctx.fillText(this.turnIndicatorText.right, turnX, turnY);

    ctx.restore();
  }

  /**
   * ⚽ Draw balls left display in lower left corner of board
   */
  drawBallsLeft(ctx: CanvasRenderingContext2D, boardBounds: { x: number, y: number, width: number, height: number }) {
    if (!this.ballsLeftVisible || this.ballsLeft <= 0) return;

    ctx.save();

    // 📝 CONFIGURABLE POSITIONING AND STYLING
    // Adjust these values to customize the display appearance and position
    const displayX = boardBounds.x + 10;  // Horizontal offset from left edge (pixels)
    const displayY = boardBounds.y + boardBounds.height - 40;  // Vertical offset from bottom (pixels)
    const ballRadius = 6;  // Size of each ball indicator (pixels)
    const ballSpacing = 15;  // Space between balls (pixels)
    const ballsLeftText = "Rollrs:";

    // Text configuration
    const textOffsetY = -25;  // Distance above balls for text (negative = above)
    const textFontSize = 10;  // Font size for "BALLS LEFT:" label
    const textGlowIntensity = 12;  // Glow blur radius for text (0 = no glow)
    const ballGlowIntensity = 8;  // Glow blur radius for balls

    // Draw "BALLS LEFT:" label with pinball-style glow effect
    const textColor = this.ballsLeftColor;  // Use same color as balls for consistency

    // Create glowing text effect (similar to other board text)
    ctx.font = `${textFontSize}px`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';

    // Draw glow layer
    ctx.shadowColor = textColor;
    ctx.shadowBlur = textGlowIntensity;
    ctx.fillStyle = textColor;
    ctx.fillText(ballsLeftText, displayX - 8, displayY + textOffsetY);

    // Draw main text (brighter, no shadow)
    ctx.shadowBlur = 0;
    ctx.fillStyle = textColor;
    ctx.fillText(ballsLeftText, displayX - 7, displayY + textOffsetY);

    // Update rolling ball animation
    this.updateRollingBallAnimation();

    // Draw remaining balls
    // Hide one light when a ball has moved from rack to launch and until the
    // next decrement occurs.
    const ballsToShow = Math.max(0, this.ballsLeft - (this.reserveAtLaunch ? 1 : 0));
    for (let i = 0; i < ballsToShow; i++) {
      const ballX = displayX + (i * ballSpacing);
      const ballY = displayY;

      // Draw ball with glow effect using game ball color
      ctx.shadowColor = this.ballsLeftColor;
      ctx.shadowBlur = ballGlowIntensity;
      ctx.fillStyle = this.ballsLeftColor;
      ctx.beginPath();
      ctx.arc(ballX, ballY, ballRadius, 0, Math.PI * 2);
      ctx.fill();

      // Add rim for definition (darker version of ball color)
      ctx.shadowBlur = 0;
      const rimColor = this.darkenColor(this.ballsLeftColor, 0.3);
      ctx.strokeStyle = rimColor;
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // Draw rolling ball if animation is active
    if (this.rollingBall?.active) {
      this.drawRollingBall(ctx, ballRadius);
    }

    ctx.restore();
  }

  /**
   * 🎯 Update rolling ball animation
   */
  private updateRollingBallAnimation() {
    if (!this.rollingBall?.active) return;

    const now = performance.now();
    const elapsed = now - this.rollingBall.startTime;

    if (elapsed >= this.rollingBall.duration) {
      // Animation complete - ball should persist at target position as the game ball
      this.rollingBall.active = false;
      // Don't set to null - keep the ball at target position for drawing

      // Apply any pending ball count update that was deferred during animation
      if (this.pendingBallsLeft !== null) {
        this.ballsLeft = this.pendingBallsLeft;
        this.pendingBallsLeft = null;
      }
    }
  }

  /**
   * 🎯 Draw the rolling ball animation
   */
  private drawRollingBall(ctx: CanvasRenderingContext2D, ballRadius: number) {
    if (!this.rollingBall) return;

    let currentX, currentY;

    if (this.rollingBall.active) {
      // Ball is animating
      const now = performance.now();
      const elapsed = now - this.rollingBall.startTime;
      const progress = Math.min(elapsed / this.rollingBall.duration, 1);

      // Ease-out animation for smooth deceleration
      const easedProgress = 1 - Math.pow(1 - progress, 3);

      // Calculate current position
      currentX = this.rollingBall.startX + (this.rollingBall.targetX - this.rollingBall.startX) * easedProgress;
      currentY = this.rollingBall.startY + (this.rollingBall.targetY - this.rollingBall.startY) * easedProgress;
    } else {
      // Ball animation is complete, show ball at target position
      currentX = this.rollingBall.targetX;
      currentY = this.rollingBall.targetY;
    }

    // Draw the ball using game ball color
    const ballColor = this.rollingBall.color;
    ctx.shadowColor = ballColor;
    ctx.shadowBlur = this.rollingBall.active ? 12 : 8; // More glow when moving
    ctx.fillStyle = ballColor;
    ctx.beginPath();
    ctx.arc(currentX, currentY, ballRadius, 0, Math.PI * 2);
    ctx.fill();

    // Add rim for definition (darker version of ball color)
    ctx.shadowBlur = 0;
    // Create a darker version of the ball color for the rim
    const darkerColor = this.darkenColor(ballColor, 0.3);
    ctx.strokeStyle = darkerColor;
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  /**
   * Helper to darken a color
   */
  private darkenColor(color: string, factor: number): string {
    // Simple darkening for hex colors
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);

      const newR = Math.floor(r * (1 - factor));
      const newG = Math.floor(g * (1 - factor));
      const newB = Math.floor(b * (1 - factor));

      return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    }
    // Fallback for non-hex colors
    return color;
  }
}
